import { useState } from "react";

export const MATH_TOOL_GROUPS = {
  common: [
    {
      heading: { en: "Operations", af: "Bewerkings" },
      symbols: ["−", "±", "×", "÷", "=", "≠", "≈"],
    },
    {
      heading: { en: "Comparisons", af: "Vergelykings" },
      symbols: ["<", ">", "≤", "≥"],
    },
    {
      heading: { en: "Powers and roots", af: "Magte en wortels" },
      symbols: ["²", "³", "^", "√(", "∛(", "⁻¹"],
    },
    {
      heading: { en: "Sets and intervals", af: "Versamelings en intervalle" },
      symbols: ["∈", "∉", "ℝ", "∅", "∪", "∩", "∞", "x ∈ (", "x ∈ ["],
    },
    {
      heading: { en: "Reasoning and symbols", af: "Redenering en simbole" },
      symbols: ["∴", "∵", "α", "β", "θ", "π", "Δ", "|x|"],
    },
  ],
  paper1: [
    {
      heading: { en: "Functions", af: "Funksies" },
      symbols: ["f(x)", "g(x)", "f⁻¹(x)", "x₁", "x₂", "y₁", "y₂"],
    },
    {
      heading: { en: "Sequences", af: "Rye" },
      symbols: ["Tₙ", "Sₙ", "aₙ", "rⁿ", "Σ"],
    },
    {
      heading: { en: "Finance", af: "Finansies" },
      symbols: ["A = P(", "1 + i", "1 − i", "n"],
    },
    {
      heading: { en: "Calculus", af: "Calculus" },
      symbols: ["f′(x)", "dy/dx", "lim(x→a)", "Δx"],
    },
    {
      heading: { en: "Probability", af: "Waarskynlikheid" },
      symbols: ["P(A)", "P(A′)", "P(A∩B)", "P(A∪B)", "n(S)", "n(A)", "n!"],
    },
  ],
  paper2: [
    {
      heading: { en: "Statistics", af: "Statistiek" },
      symbols: ["x̄", "ŷ", "Σ", "σ", "Q₁", "Q₂", "Q₃", "IQR"],
    },
    {
      heading: { en: "Coordinates", af: "Koördinate" },
      symbols: ["(x₁ ; y₁)", "(x₂ ; y₂)", "m ="],
    },
    {
      heading: { en: "Trigonometry", af: "Trigonometrie" },
      symbols: ["sin", "cos", "tan", "sin⁻¹", "cos⁻¹", "tan⁻¹", "°"],
    },
    {
      heading: { en: "Geometry", af: "Meetkunde" },
      symbols: ["∠", "△", "⊥", "∥", "≅", "∼", "⊙", "°"],
    },
  ],
};

const TAB_LABELS = {
  common: { en: "Common", af: "Algemeen" },
  paper1: { en: "Paper 1", af: "Vraestel 1" },
  paper2: { en: "Paper 2", af: "Vraestel 2" },
};

function MathToolkit({
  language,
  paperNumber,
  onInsertSymbol,
}) {
  const [showTools, setShowTools] = useState(false);
  const [activeTab, setActiveTab] = useState(
    paperNumber === 2 ? "paper2" : "paper1"
  );

  return (
    <div
      style={{
        marginTop: "20px",
        marginBottom: "15px",
        padding: "15px",
        borderRadius: "10px",
        background: "white",
      }}
    >
      <button
        type="button"
        aria-expanded={showTools}
        onClick={() => setShowTools((value) => !value)}
        style={{
          padding: "12px",
          border: "none",
          borderRadius: "10px",
          background: "#2563eb",
          color: "white",
          cursor: "pointer",
          fontSize: "18px",
        }}
      >
        {language === "af"
          ? "📐 Wiskundehulpmiddels"
          : "📐 Math Toolkit"}
      </button>

      {showTools && (
        <div style={{ marginTop: "15px" }}>
          <a
            href="https://www.education.gov.za/LinkClick.aspx?fileticket=gtM--fFe--Q%3D&mid=9706&portalid=0&tabid=2720"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-block",
              padding: "10px 14px",
              border: "1px solid #bfdbfe",
              borderRadius: "8px",
              background: "#eff6ff",
              color: "#1d4ed8",
              fontWeight: "bold",
              textDecoration: "none",
            }}
          >
            {language === "af"
              ? "📄 Maak die DBE-inligtings- en formuleblad oop"
              : "📄 Open the DBE information and formula sheet"}
          </a>

          <div
            role="tablist"
            aria-label={
              language === "af"
                ? "Wiskundehulpmiddel-afdelings"
                : "Math Toolkit sections"
            }
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              marginTop: "18px",
            }}
          >
            {Object.keys(TAB_LABELS).map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={activeTab === tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: "9px 12px",
                  borderRadius: "8px",
                  border: "1px solid #93c5fd",
                  background:
                    activeTab === tab
                      ? "#dbeafe"
                      : "white",
                  color: "#1e3a8a",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                {TAB_LABELS[tab][language]}
              </button>
            ))}
          </div>

          {MATH_TOOL_GROUPS[activeTab].map(
            (group, groupIndex) => (
              <section key={`${activeTab}-${groupIndex}`}>
                <h4
                  style={{
                    marginTop: "22px",
                    marginBottom: "8px",
                  }}
                >
                  {group.heading[language]}
                </h4>
                <div>
                  {group.symbols.map((symbol, index) => (
                    <button
                      key={`${symbol}-${index}`}
                      type="button"
                      aria-label={
                        language === "af"
                          ? `Voeg ${symbol} in`
                          : `Insert ${symbol}`
                      }
                      title={
                        language === "af"
                          ? `Voeg ${symbol} by jou antwoord in`
                          : `Insert ${symbol} into your answer`
                      }
                      onClick={() => onInsertSymbol(symbol)}
                      style={{
                        minWidth: "58px",
                        margin: "5px",
                        padding: "10px 14px",
                        border: "1px solid #d1d5db",
                        borderRadius: "10px",
                        background: "white",
                        cursor: "pointer",
                        fontSize: "20px",
                      }}
                    >
                      {symbol}
                    </button>
                  ))}
                </div>
              </section>
            )
          )}

          <p style={{ marginTop: "18px", color: "#4b5563" }}>
            {language === "af"
              ? "Sleutelbordwenk: Jy kan ook x^2 vir x² en sqrt(x) vir √(x) tik."
              : "Keyboard tip: You may also type x^2 for x² and sqrt(x) for √(x)."}
          </p>
        </div>
      )}
    </div>
  );
}

export default MathToolkit;
